import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import MoverAvailabilityEditor from '../../../components/MoverAvailabilityEditor.jsx';
import { PROVINCE_TIMEZONES } from '../geography.js';

const TIMEZONE_LABELS = {
  'America/Vancouver': 'Pacific Time (BC)',
  'America/Edmonton': 'Mountain Time (Alberta)',
  'America/Regina': 'Central Time (Saskatchewan)',
  'America/Winnipeg': 'Central Time (Manitoba)',
  'America/Toronto': 'Eastern Time (Ontario)',
  'America/Montreal': 'Eastern Time (Quebec)',
  'America/Halifax': 'Atlantic Time',
  'America/Moncton': 'Atlantic Time',
  "America/St_Johns": 'Newfoundland Time',
  'America/Whitehorse': 'Pacific Time (Yukon)',
  'America/Yellowknife': 'Mountain Time (Northwest Territories)',
  'America/Iqaluit': 'Eastern Time (Nunavut)',
};

export default function AvailabilityStep({ answers, token, onChange }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const suggested = PROVINCE_TIMEZONES[answers.serviceArea.homeProvince] || 'America/Toronto';
  const timezone = answers.availability.timezone || suggested;

  const weeklySummary = useMemo(() => {
    const weekly = answers.availability.weekly;
    if (!weekly) return 'Default schedule: every day, 8:00 AM – 6:00 PM';
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const open = days.filter(d => (weekly[d] || []).length > 0);
    if (open.length === 7) return 'Available 7 days a week';
    if (!open.length) return 'No working days configured';
    return `Available ${open.length} day${open.length > 1 ? 's' : ''} a week (${open.map(d => d.slice(0, 3)).join(', ')})`;
  }, [answers.availability.weekly]);

  return (
    <>
      <Typography variant="h5" fontWeight={600} gutterBottom>When are you available?</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Customers can only book you during your working hours. You can block holidays or vacations anytime.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>YOUR TIMEZONE</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Your availability uses <strong>{TIMEZONE_LABELS[timezone] || timezone}</strong>
          {answers.serviceArea.homeProvince ? `, based on your business location.` : '.'}
        </Typography>
        <TextField select size="small" label="Confirm or change timezone" value={timezone}
          onChange={e => onChange({ ...answers, availability: { ...answers.availability, timezone: e.target.value } })}
          sx={{ minWidth: 280 }}>
          {Object.entries(TIMEZONE_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </TextField>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>WEEKLY SCHEDULE</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{weeklySummary}</Typography>
        <Button variant="outlined" onClick={() => setEditorOpen(true)}>
          Edit working hours & days off
        </Button>
      </Paper>

      {!answers.availability.weekly && (
        <Alert severity="info" sx={{ mt: 2.5 }}>
          We'll start you with the standard schedule (every day, 8 AM – 6 PM). Adjust it whenever you like.
        </Alert>
      )}

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Working hours & days off</DialogTitle>
        <DialogContent>
          <MoverAvailabilityEditor token={token} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
